import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, AtSign } from 'lucide-react';
import { SiInstagram } from 'react-icons/si';
import { Banner, Card, EmptyState, PageHeader, initialsOf } from '../design/ui';

// Return of the Instagram OAuth (backend redirect, social_media.py): the
// person picks which of their Instagram business accounts opens tickets.
const InstagramAccountSelection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState('');
  const [selecting, setSelecting] = useState(null);

  const queryParams = new URLSearchParams(location.search);
  const accountsJson = queryParams.get('accounts');

  useEffect(() => {
    if (!accountsJson) return;
    try {
      setAccounts(JSON.parse(accountsJson));
    } catch (parseError) {
      setError(t('instagramAccountSelection.failedToConnect', 'Impossible de lire les comptes Instagram.'));
    }
  }, [accountsJson, t]);

  const handleAccountSelect = async (accountId, accountName, access_token, instagram_business_account) => {
    try {
      setSelecting(accountId);
      setError('');
      const token = localStorage.getItem('access_token');
      await axios.post('/api/social/instagram/select_account', {
        account_id: accountId,
        name: accountName,
        access_token: access_token,
        instagram_business_account: instagram_business_account
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      navigate('/parameters?status=success');
    } catch (selectError) {
      console.error('Failed to select Instagram account:', selectError?.response?.status);
      setError(t('instagramAccountSelection.failedToConnect', 'La connexion du compte Instagram a échoué.'));
    } finally {
      setSelecting(null);
    }
  };

  return (
    <div className="u-page u-page--narrow">
      <PageHeader
        breadcrumb={(
          <a className="u-link" href="/parameters?tab=integrations">
            <ArrowLeft size={14} aria-hidden="true" /> {t('publicUi.backToChannels', 'Canaux et boîtes mail')}
          </a>
        )}
        title={t('instagramAccountSelection.title', 'Choisir le compte Instagram')}
        subtitle={t('publicUi.instagramPickSub', 'Les messages privés du compte choisi deviendront des tickets.')}
      />
      <div className="u-well">
        {error && <Banner tone="danger">{error}</Banner>}
        <Card
          icon={SiInstagram}
          title={t('publicUi.instagramAccounts', 'Comptes Instagram professionnels')}
          badge={accounts.length > 0 && <span className="u-badge u-badge--accent">{accounts.length}</span>}
          flush
        >
          {accounts.length === 0 ? (
            <EmptyState icon={AtSign} title={t('publicUi.instagramNone', 'Aucun compte reçu d’Instagram')}>
              {t('publicUi.instagramNoneHelp', 'Relancez la connexion depuis Paramètres › Canaux et boîtes mail, en autorisant au moins un compte professionnel.')}
            </EmptyState>
          ) : (
            <ul className="u-list">
              {accounts.map((account) => (
                <li className="u-row" key={account.id}>
                  <span className="u-avatar u-avatar--sm" aria-hidden="true">{initialsOf(account.name)}</span>
                  <div className="u-row__main">
                    <div className="u-row__title">{account.name}</div>
                  </div>
                  <div className="u-row__side">
                    <button
                      type="button"
                      className="u-btn u-btn--primary u-btn--sm"
                      disabled={selecting !== null}
                      onClick={() => handleAccountSelect(account.id, account.name, account.access_token, account.instagram_business_account?.id)}
                    >
                      {selecting === account.id ? t('userParameter.connecting', 'Connexion…') : t('settingsPage.connect', 'Connecter')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
};

export default InstagramAccountSelection;
